import React, { useState, useEffect } from "react";
import {
  AppShell,
  Group,
  Text,
  Avatar,
  MantineProvider,
  createTheme,
  Tabs,
  Box,
  UnstyledButton,
  Menu,
  Breadcrumbs,
  Anchor,
  NavLink,
  ScrollArea,
} from "@mantine/core";
import {
  IconLayoutDashboard,
  IconRocket,
  IconShoppingBag,
  IconBrain,
  IconSpeakerphone,
  IconLogout,
  IconSettings,
  IconChartBar,
  IconChevronRight,
  IconBell,
  IconChartPie,
  IconTicket,
  IconBuildingStore,
  IconUsers,
  IconVideo,
  IconCalendarEvent,
  IconStar,
  IconBuildingCommunity,
  IconShieldExclamation,
  IconCoin,
} from "@tabler/icons-react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import logo from "../../assets/Logo.jpeg"; // Ensure path is correct
import "@mantine/core/styles.css";

const theme = createTheme({
  primaryColor: "blue",
  fontFamily: "Inter, system-ui, sans-serif",
  defaultRadius: "md",
});

export default function AdminDashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  // --- USER STATE ---
  const [user, setUser] = useState({
    nickname: "Admin",
    role: "Administrator",
  });

  useEffect(() => {
    const storedUser = localStorage.getItem("adminUser");

    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser({
          nickname:
            parsedUser.nickname || parsedUser.email?.split("@")[0] || "Admin",
          role: parsedUser.role || "ADMIN",
        });
      } catch (error) {
        console.error("Failed to load user profile", error);
      }
    }
  }, []);

  const getInitials = (name) => {
    return name
      ? name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .slice(0, 2)
      : "AD";
  };

  // Helper to determine which "Department" is active
  const getActiveCategory = () => {
    const path = location.pathname;
    if (path.includes("project-management") || path.includes("ideas"))
      return "voting";
    if (path.includes("events")) return "events";
    if (path.includes("product-management")) return "shop";
    if (path.includes("quiz")) return "quiz";
    if (path.includes("users")) return "users";
    if (path.includes("notifications") || path.includes("global-banner"))
      return "marketing";
    if (path.includes("feeds")) return "feeds";
    if (
      path.includes("creator-applications") ||
      path.includes("community-management") ||
      path.includes("hub-earnings")
    )
      return "creators";
    if (path.includes("ugc-reports")) return "moderation";
    return "dashboard";
  };

  const activeTab = getActiveCategory();

  // The Dynamic Sidebar Data
  const subMenus = {
    dashboard: [
      {
        label: "Overview",
        path: "/admin/dashboard",
        icon: IconChartPie,
      },
      {
        label: "Analytics & Insights",
        path: "/admin/dashboard/analytics",
        icon: IconChartBar,
      },
    ],
    users: [
      { label: "All Users", path: "/admin/dashboard/users", icon: IconUsers },
    ],
    voting: [
      {
        label: "Ideas Board",
        path: "/admin/dashboard/project-management",
        icon: IconRocket,
      },
      {
        label: "Idea Banners",
        path: "/admin/dashboard/project-management/ideas-banner-management",
        icon: IconSpeakerphone,
      },
    ],
    shop: [
      {
        label: "Inventory & Events",
        path: "/admin/dashboard/product-management",
        icon: IconShoppingBag,
      },
      {
        label: "Shopfronts",
        path: "/admin/dashboard/product-management/shopfronts",
        icon: IconBuildingStore,
      },
    ],
    quiz: [
      {
        label: "Standard Quiz",
        path: "/admin/dashboard/quiz-management",
        icon: IconBrain,
      },
      {
        label: "Mega Quiz",
        path: "/admin/dashboard/mega-quiz-management",
        icon: IconTicket,
      },
    ],
    marketing: [
      {
        label: "Push Notifications",
        path: "/admin/dashboard/marketing/notifications",
        icon: IconBell,
      },
      {
        label: "Global Banners",
        path: "/admin/dashboard/global-banner-management",
        icon: IconLayoutDashboard,
      },
    ],
    events: [
      {
        label: "Booth Application Review",
        path: "/admin/dashboard/events/applications",
        icon: IconCalendarEvent,
      },
    ],
    feeds: [
      {
        label: "Content Management",
        path: "/admin/dashboard/feeds",
        icon: IconVideo,
      },
    ],
    creators: [
      {
        label: "Creator Applications",
        path: "/admin/dashboard/creator-applications",
        icon: IconStar,
      },
      {
        label: "Community Management",
        path: "/admin/dashboard/community-management",
        icon: IconBuildingCommunity,
      },
      {
        label: "Earnings & Settlements",
        path: "/admin/dashboard/hub-earnings",
        icon: IconCoin,
      },
    ],
    moderation: [
      {
        label: "UGC Reports",
        path: "/admin/dashboard/ugc-reports",
        icon: IconShieldExclamation,
      },
    ],
  };

  const getBreadcrumbLabel = () => {
    const currentMenu = subMenus[activeTab];
    if (!currentMenu) return "Details";

    const activeItem = currentMenu
      .filter((item) => location.pathname.includes(item.path))
      .sort((a, b) => b.path.length - a.path.length)[0];

    return activeItem ? activeItem.label : "Details";
  };

  const activeBreadcrumbLabel = getBreadcrumbLabel();

  return (
    <MantineProvider theme={theme}>
      <AppShell
        header={{ height: 64 }}
        navbar={{ width: 250, breakpoint: "sm" }}
        padding="md"
        bg="#f8f9fa"
      >
        <AppShell.Header withBorder={true}>
          <Group h="100%" px="md" justify="space-between">
            {/* LEFT: Logo & Primary Tabs */}
            <Group gap="xl" h="100%">
              <Group
                gap="xs"
                style={{ cursor: "pointer" }}
                onClick={() => navigate("/admin/dashboard")}
              >
                <Avatar src={logo} size={32} radius="md" />
                <Box visibleFrom="sm">
                  <Text
                    fw={900}
                    size="sm"
                    className="ota-gradient-text"
                    lh={1.1}
                  >
                    OTA ADMIN
                  </Text>
                  <Text size="10px" c="dimmed" fw={600} tt="uppercase">
                    Portal
                  </Text>
                </Box>
              </Group>

              <Tabs
                value={activeTab}
                onChange={(v) => {
                  const targetPath = subMenus[v]?.[0]?.path;
                  if (targetPath) navigate(targetPath);
                }}
                variant="pills"
                radius="xl"
                visibleFrom="sm"
                classNames={{
                  list: "gap-1",
                  tab: "fw-600 text-xs h-[32px] data-[active]:bg-blue-50 data-[active]:text-blue-700",
                }}
              >
                <Tabs.List>
                  <Tabs.Tab value="dashboard">Overview</Tabs.Tab>
                  <Tabs.Tab value="voting">Voting</Tabs.Tab>
                  <Tabs.Tab value="shop">Shop</Tabs.Tab>
                  <Tabs.Tab value="quiz">Quiz</Tabs.Tab>
                  <Tabs.Tab value="feeds">Feeds</Tabs.Tab>
                  <Tabs.Tab value="marketing">Marketing</Tabs.Tab>
                  <Tabs.Tab value="events">Events</Tabs.Tab>
                  <Tabs.Tab value="creators">Creators</Tabs.Tab>
                  <Tabs.Tab value="moderation">Moderation</Tabs.Tab>
                  {user.role === "SUPER_ADMIN" && (
                    <Tabs.Tab value="users">Users</Tabs.Tab>
                  )}
                </Tabs.List>
              </Tabs>
            </Group>

            {/* RIGHT: User Profile (DYNAMIC) */}
            <Group>
              <Menu shadow="md" width={200} position="bottom-end">
                <Menu.Target>
                  <UnstyledButton>
                    <Group gap={8}>
                      <Avatar color="blue" radius="xl" size="sm">
                        {getInitials(user.nickname)}
                      </Avatar>
                      <Box visibleFrom="sm">
                        <Text size="xs" fw={700}>
                          {user.nickname}
                        </Text>
                        <Text size="10px" c="dimmed" tt="uppercase">
                          {user.role}
                        </Text>
                      </Box>
                    </Group>
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Settings</Menu.Label>

                  <Menu.Item
                    color="red"
                    leftSection={<IconLogout size={14} />}
                    onClick={() => {
                      localStorage.clear();
                      navigate("/admin");
                    }}
                  >
                    Logout
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Group>
        </AppShell.Header>

        <AppShell.Navbar p="md" bg="white" withBorder={true}>
          <Box mb="md">
            <Text
              size="10px"
              fw={700}
              c="dimmed"
              tt="uppercase"
              lts={1}
              mb="xs"
            >
              {activeTab} Management
            </Text>
          </Box>

          <ScrollArea className="flex-1 -mx-md px-md">
            {subMenus[activeTab]?.map((item) => {
              // Smart active logic
              const isExact = location.pathname === item.path;
              const isParent =
                location.pathname.startsWith(item.path) &&
                item.path !== "/admin/dashboard";
              const hasMoreSpecificMatch = subMenus[activeTab].some(
                (other) =>
                  other !== item &&
                  other.path.length > item.path.length &&
                  location.pathname.startsWith(other.path),
              );
              const isActive = isExact || (isParent && !hasMoreSpecificMatch);

              return (
                <NavLink
                  key={item.path}
                  label={item.label}
                  leftSection={<item.icon size={18} stroke={1.5} />}
                  active={isActive}
                  onClick={() => navigate(item.path)}
                  variant="light"
                  color="blue"
                  className="rounded-md mb-1"
                  styles={{
                    root: {
                      fontWeight: 500,
                      color: isActive ? "#1c7ed6" : "#495057",
                    },
                    section: { marginRight: "12px" },
                  }}
                />
              );
            })}
          </ScrollArea>
        </AppShell.Navbar>

        <AppShell.Main>
          <Box mb="lg">
            <Breadcrumbs
              separator={
                <IconChevronRight size={12} className="text-gray-400" />
              }
            >
              <Anchor
                size="xs"
                c="dimmed"
                onClick={() => navigate("/admin/dashboard")}
              >
                Home
              </Anchor>
              <Text size="xs" c="dimmed" tt="capitalize">
                {activeTab}
              </Text>
              <Text size="xs" fw={600} c="blue">
                {activeBreadcrumbLabel}
              </Text>
            </Breadcrumbs>
          </Box>

          <Box
            bg="white"
            p="xl"
            style={{
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              border: "1px solid #e9ecef",
              minHeight: "75vh",
            }}
          >
            <Outlet />
          </Box>
        </AppShell.Main>
      </AppShell>
    </MantineProvider>
  );
}
